import { useMemo } from "react";

import { IFolder, WorkspaceFilterItem, WorkspaceFilters, WorkspaceFiltersFromDB } from "store/types";

const FILTER_TYPES: Record<string, "date" | "single" | "raw" | "uppercase"> = {
  last_updated: "date",
  shared_with: "single",
  file_type: "uppercase"
};

const mergeFilters = (regular: WorkspaceFilters = {}, dataroom: WorkspaceFilters = {}): WorkspaceFilters => {
  const merged: WorkspaceFilters = { ...regular };
  for (const key of Object.keys(dataroom)) {
    const existing = merged[key];
    const incoming = dataroom[key];
    merged[key] = existing ? Array.from(new Set([...existing, ...incoming] as WorkspaceFilterItem[])) : incoming;
  }
  return merged;
};

interface UseWorkspaceFilterParams {
  currentFolder: IFolder | null;
  workspaceFiltersFromDB?: WorkspaceFiltersFromDB;
  /** Skip the forced "Shared With" filter (e.g. client workspace, which has no sharing). */
  hideShareWith?: boolean;
}

export const useWorkspaceFilter = ({
  currentFolder,
  workspaceFiltersFromDB = {},
  hideShareWith = false
}: UseWorkspaceFilterParams) => {
  const isDataroom = currentFolder?.kind === "dataroom";
  const isAggregateView = !currentFolder;

  const filtersList = useMemo(() => {
    const { regular_folder, dataroom_folder } = workspaceFiltersFromDB;

    let base: WorkspaceFilters;
    if (isAggregateView) {
      // all files or recently added files
      base = mergeFilters(regular_folder, dataroom_folder);
    } else if (isDataroom) {
      //filters for cloud mirror folder
      base = { ...dataroom_folder };
    } else {
      //filters for regular folder
      base = { ...regular_folder };
    }

    if (!hideShareWith) {
      base.shared_with = ["Yes", "No"];
    }
    return base;
  }, [isDataroom, isAggregateView, workspaceFiltersFromDB, hideShareWith]);

  const dataroomOnlyKeys = useMemo(() => {
    const regularKeys = new Set(Object.keys(workspaceFiltersFromDB.regular_folder || {}));
    return Object.keys(workspaceFiltersFromDB.dataroom_folder || {}).filter((key) => !regularKeys.has(key));
  }, [workspaceFiltersFromDB.dataroom_folder, workspaceFiltersFromDB.regular_folder]);

  // Drop the shared_with type too, so the filter chip itself is hidden (not just its options).
  const filterTypes = useMemo(() => {
    if (!hideShareWith) return FILTER_TYPES;
    const { shared_with: _sharedWith, ...rest } = FILTER_TYPES;
    return rest;
  }, [hideShareWith]);

  return { filtersList, dataroomOnlyKeys, isDataroom, isAggregateView, filterTypes };
};
